import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as echarts from 'echarts';
import 'echarts-gl';
import type { ECharts } from 'echarts';
import {
  CHINA_GEO_JSON_URL,
  containerStyle,
  loadingStyle,
  mapActionBtnStyle,
  mapBackBtnWrapStyle,
  provinceLoadingStyle,
} from './mapConstants';
import {
  buildOption,
  buildPolicyScatterNational,
  buildPolicyScatterProvince,
  buildScatterData,
  buildViewContext,
  featureLngLatByName,
  fetchProvinceCacheEntry,
  matchCityFeatureName,
  safeFlushPointerThenApply,
  subscribeMapInteractions,
  syncGeo3DViewCenter,
} from './mapUtils';
import { resolveProvinceAdcodeLoose } from './provinceAdcode';
import type {
  DrillState,
  EchartsMapProps,
  MapLayoutMode,
  MapStatsScope,
  ProvinceCacheEntry,
  ProvinceGeoJson,
} from './types';

export type { EchartsMapProps } from './types';

export function EchartsMap(props: EchartsMapProps): React.ReactElement {
  const {
    parks,
    selectedParkId,
    onSelectPark,
    policyFilter,
    policiesForMap = [],
    selectedPolicyId,
    policySidebarSelectTick = 0,
    onPolicyMapDrillProvinceName,
    onPolicyMapSelectCityName,
    mapQueryMode = 'parks',
    onMapStatsScopeChange,
    onBackToNational,
    onDrillProvince,
    onRestoreDefaults,
    layoutChromeRightInsetPx = 316,
  } = props;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<ECharts | null>(null);
  const onSelectRef = useRef(onSelectPark);
  onSelectRef.current = onSelectPark;
  const onDrillProvinceRef = useRef(onDrillProvince);
  onDrillProvinceRef.current = onDrillProvince;
  const onMapStatsScopeRef = useRef(onMapStatsScopeChange);
  onMapStatsScopeRef.current = onMapStatsScopeChange;

  const onPolicyDrillProvinceNameRef = useRef(onPolicyMapDrillProvinceName);
  onPolicyDrillProvinceNameRef.current = onPolicyMapDrillProvinceName;
  const onPolicySelectCityNameRef = useRef(onPolicyMapSelectCityName);
  onPolicySelectCityNameRef.current = onPolicyMapSelectCityName;

  const mapQueryModeRef = useRef(mapQueryMode);
  mapQueryModeRef.current = mapQueryMode;
  const policyLevelRef = useRef(policyFilter?.level ?? 'all');
  policyLevelRef.current = policyFilter?.level ?? 'all';
  const policyProvinceRef = useRef(policyFilter?.province);
  policyProvinceRef.current = policyFilter?.province;

  const policyDrillByUserRef = useRef(false);
  const provinceGeoForPickRef = useRef<ProvinceGeoJson | null>(null);
  const prevPolicyFilterProvince = useRef<string | undefined>(undefined);
  const selectedPolicyIdRef = useRef<string | null | undefined>(selectedPolicyId);
  selectedPolicyIdRef.current = selectedPolicyId;
  const pendingSidebarCityCenterRef = useRef<string | null>(null);

  const provinceCacheRef = useRef<Map<string, ProvinceCacheEntry>>(new Map());

  const [mapReady, setMapReady] = useState(false);
  const [chinaGeoJson, setChinaGeoJson] = useState<ProvinceGeoJson | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [drill, setDrill] = useState<DrillState>({ kind: 'idle' });
  const [focusAdcode, setFocusAdcode] = useState<string | null>(null);
  const [layoutMode, setLayoutMode] = useState<MapLayoutMode>('nationalOverview');
  const [drillTargetCoord, setDrillTargetCoord] = useState<[number, number] | null>(null);

  const highlightCityGeoName = useMemo(() => {
    const ct = policyFilter?.city?.trim();
    if (!ct || layoutMode !== 'provinceDrill' || !focusAdcode) return null;
    if (drill.kind !== 'ready' || drill.adcode !== focusAdcode) return null;
    const geoJson = provinceCacheRef.current.get(focusAdcode)?.geoJson;
    if (!geoJson) return ct;
    return matchCityFeatureName(ct, geoJson) ?? ct;
  }, [policyFilter?.city, layoutMode, focusAdcode, drill]);

  const viewCtx = useMemo(
    () =>
      buildViewContext(
        parks,
        drill,
        focusAdcode,
        layoutMode,
        drillTargetCoord,
        highlightCityGeoName,
      ),
    [parks, drill, focusAdcode, layoutMode, drillTargetCoord, highlightCityGeoName],
  );

  useEffect(() => {
    const scope: MapStatsScope = {
      mode: layoutMode,
      provinceAdcode: focusAdcode,
      highlightedCityName:
        mapQueryMode === 'policies' && policyFilter?.city?.trim()
          ? policyFilter.city.trim()
          : null,
    };
    onMapStatsScopeRef.current?.(scope);
  }, [layoutMode, focusAdcode, mapQueryMode, policyFilter?.city]);

  const viewCtxRef = useRef(viewCtx);
  viewCtxRef.current = viewCtx;

  const scatterData = useMemo(
    () => buildScatterData(viewCtx.scatterParks, selectedParkId ?? null),
    [viewCtx.scatterParks, selectedParkId],
  );

  const scatterDataRef = useRef(scatterData);
  scatterDataRef.current = scatterData;

  const policyScatterData = useMemo(() => {
    if (mapQueryMode !== 'policies' || !policiesForMap.length) return [];
    if (layoutMode === 'provinceDrill' && drill.kind === 'ready' && focusAdcode) {
      const geo = provinceCacheRef.current.get(focusAdcode)?.geoJson;
      if (!geo) return [];
      return buildPolicyScatterProvince(
        policiesForMap,
        geo,
        focusAdcode,
        selectedPolicyId ?? null,
      );
    }
    if (chinaGeoJson) {
      return buildPolicyScatterNational(
        policiesForMap,
        chinaGeoJson,
        selectedPolicyId ?? null,
      );
    }
    return [];
  }, [
    mapQueryMode,
    policiesForMap,
    layoutMode,
    drill,
    focusAdcode,
    chinaGeoJson,
    selectedPolicyId,
  ]);

  const policyScatterDataRef = useRef(policyScatterData);
  policyScatterDataRef.current = policyScatterData;

  const initChart = useCallback(() => {
    const el = containerRef.current;
    if (!el || chartRef.current) return;
    chartRef.current = echarts.init(el);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    fetch(CHINA_GEO_JSON_URL)
      .then((r) => {
        if (!r.ok) throw new Error(`地图数据加载失败 ${r.status}`);
        return r.json() as Promise<ProvinceGeoJson>;
      })
      .then((geoJson) => {
        if (cancelled) return;
        echarts.registerMap('china', geoJson as never);
        setChinaGeoJson(geoJson);
        setMapReady(true);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : '地图加载失败';
        setLoadError(msg);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (mapQueryMode !== 'policies' || !policyFilter) return;

    const p = policyFilter.province?.trim();
    const c = policyFilter.city?.trim();
    const ad = p ? resolveProvinceAdcodeLoose(p) : null;

    if (
      prevPolicyFilterProvince.current &&
      p &&
      prevPolicyFilterProvince.current !== p
    ) {
      policyDrillByUserRef.current = false;
    }
    prevPolicyFilterProvince.current = p;

    if (!p || !ad) {
      if (policyDrillByUserRef.current && layoutMode === 'provinceDrill' && focusAdcode) {
        return;
      }
      setLayoutMode('nationalOverview');
      setFocusAdcode(null);
      setDrillTargetCoord(null);
      policyDrillByUserRef.current = false;
      return;
    }

    setFocusAdcode(ad);

    if (c) {
      setLayoutMode('provinceDrill');
      return;
    }

    if (
      policyDrillByUserRef.current &&
      layoutMode === 'provinceDrill' &&
      focusAdcode === ad
    ) {
      return;
    }

    setLayoutMode('nationalOverview');
    setDrillTargetCoord(null);
  }, [mapQueryMode, policyFilter, layoutMode, focusAdcode]);

  useEffect(() => {
    if (mapQueryMode !== 'policies') return;
    const c = policyFilter?.city?.trim();
    if (!c || layoutMode !== 'provinceDrill' || !focusAdcode) return;
    if (drill.kind !== 'ready' || drill.adcode !== focusAdcode) return;
    const entry = provinceCacheRef.current.get(focusAdcode);
    if (!entry) return;
    const geoName = matchCityFeatureName(c, entry.geoJson);
    if (!geoName) return;
    const ll = featureLngLatByName(entry.geoJson, geoName);
    if (ll) setDrillTargetCoord(ll);
  }, [mapQueryMode, policyFilter?.city, layoutMode, focusAdcode, drill]);

  /** 侧栏列表选中政策：下钻至该省并居中到对应市；国家级政策在省模式下恢复全省居中 */
  useEffect(() => {
    if (mapQueryMode !== 'policies') return;
    if (!policySidebarSelectTick) return;
    const id = selectedPolicyIdRef.current;
    if (!id) return;
    const pol = policiesForMap.find((x) => x.id === id);

    if (!pol || pol.level === 'national' || pol.level === 'park') {
      pendingSidebarCityCenterRef.current = null;
      if (layoutMode === 'provinceDrill') {
        setDrillTargetCoord(null);
      }
      return;
    }

    const ad = resolveProvinceAdcodeLoose(pol.province);
    if (!ad) {
      pendingSidebarCityCenterRef.current = null;
      return;
    }

    const city = pol.city?.trim() || null;
    const alreadyInSameProvince =
      layoutMode === 'provinceDrill' && focusAdcode === ad && drill.kind === 'ready';

    if (alreadyInSameProvince) {
      if (city) {
        const entry = provinceCacheRef.current.get(ad);
        if (entry) {
          const geoName = matchCityFeatureName(city, entry.geoJson);
          if (geoName) {
            const ll = featureLngLatByName(entry.geoJson, geoName);
            if (ll) setDrillTargetCoord(ll);
          }
        }
      } else {
        setDrillTargetCoord(null);
      }
      pendingSidebarCityCenterRef.current = null;
      return;
    }

    policyDrillByUserRef.current = true;
    pendingSidebarCityCenterRef.current = city;
    setFocusAdcode(ad);
    setLayoutMode('provinceDrill');
    if (!city) {
      setDrillTargetCoord(null);
    }
  }, [mapQueryMode, policySidebarSelectTick, policiesForMap, layoutMode, focusAdcode, drill]);

  /** 侧栏选中带「市」的政策：省下钻就绪后将视角移至该市 */
  useEffect(() => {
    if (mapQueryMode !== 'policies') return;
    if (drill.kind !== 'ready' || !focusAdcode) return;
    const city = pendingSidebarCityCenterRef.current;
    if (!city) return;
    const entry = provinceCacheRef.current.get(focusAdcode);
    if (!entry) return;
    const geoName = matchCityFeatureName(city, entry.geoJson);
    if (!geoName) {
      pendingSidebarCityCenterRef.current = null;
      return;
    }
    const ll = featureLngLatByName(entry.geoJson, geoName);
    if (ll) setDrillTargetCoord(ll);
    pendingSidebarCityCenterRef.current = null;
  }, [mapQueryMode, drill, focusAdcode]);

  useEffect(() => {
    if (mapQueryMode === 'policies') return;
    if (!selectedParkId) return;
    const sel = parks.find((p) => p.id === selectedParkId);
    if (!sel) return;
    const ad = resolveProvinceAdcodeLoose(sel.province);
    if (!ad) return;

    setDrillTargetCoord([sel.longitude, sel.latitude]);
    setFocusAdcode(ad);
    setLayoutMode('provinceDrill');
  }, [mapQueryMode, selectedParkId, parks]);

  useEffect(() => {
    if (!mapReady) return;
    if (layoutMode !== 'provinceDrill' || !focusAdcode) {
      setDrill({ kind: 'idle' });
      return;
    }

    let cancelled = false;
    const adcode = focusAdcode;
    const mapName = `province_${adcode}`;

    setDrill({ kind: 'loading', adcode });

    const finishReady = (entry: ProvinceCacheEntry) => {
      if (cancelled) return;
      echarts.registerMap(entry.mapName, entry.geoJson as never);
      setDrill({
        kind: 'ready',
        adcode,
        mapName: entry.mapName,
        centroid: entry.centroid,
        spanDeg: entry.spanDeg,
        lngSpan: entry.lngSpan,
        latSpan: entry.latSpan,
      });
    };

    const cached = provinceCacheRef.current.get(adcode);
    if (cached) {
      queueMicrotask(() => finishReady(cached));
      return () => {
        cancelled = true;
      };
    }

    fetchProvinceCacheEntry(adcode, mapName)
      .then((entry) => {
        if (cancelled) return;
        provinceCacheRef.current.set(adcode, entry);
        finishReady(entry);
      })
      .catch(() => {
        if (!cancelled) {
          setDrill({ kind: 'idle' });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [mapReady, layoutMode, focusAdcode]);

  useEffect(() => {
    if (drill.kind === 'ready') {
      const e = provinceCacheRef.current.get(drill.adcode);
      provinceGeoForPickRef.current = e?.geoJson ?? null;
    } else {
      provinceGeoForPickRef.current = null;
    }
  }, [drill]);

  useEffect(() => {
    if (!mapReady) return;
    initChart();
    const chart = chartRef.current;
    if (!chart) return;
    return subscribeMapInteractions(chart, {
      viewCtxRef,
      onSelectRef,
      onDrillProvinceRef,
      setFocusAdcode,
      setLayoutMode,
      setDrillTargetCoord,
      mapQueryModeRef,
      onPolicyDrillProvinceNameRef,
      onPolicySelectCityNameRef,
      policyDrillByUserRef,
      provinceGeoForPickRef,
      scatterDataRef,
      policyScatterDataRef,
      policyLevelRef,
      policyProvinceRef,
    });
  }, [mapReady, initChart]);

  useEffect(() => {
    if (!mapReady || !chartRef.current) return;
    const chart = chartRef.current;
    const opt = buildOption(
      scatterData,
      selectedParkId ?? null,
      viewCtx,
      policyScatterData,
    );
    safeFlushPointerThenApply(chart, () => {
      chart.setOption(opt, { notMerge: true });
      syncGeo3DViewCenter(chart, viewCtx.targetLngLat);
    });
  }, [mapReady, scatterData, selectedParkId, viewCtx, policyScatterData]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const fit = () => {
      chart.resize();
      syncGeo3DViewCenter(chart, viewCtxRef.current.targetLngLat);
    };

    window.addEventListener('resize', fit);

    const el = containerRef.current;
    let ro: ResizeObserver | undefined;
    if (el && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => fit());
      ro.observe(el);
    }

    return () => {
      window.removeEventListener('resize', fit);
      ro?.disconnect();
    };
  }, [mapReady]);

  useEffect(() => {
    return () => {
      chartRef.current?.dispose();
      chartRef.current = null;
    };
  }, []);

  return (
    <div style={containerStyle}>
      {(!mapReady && !loadError) || loadError ? (
        <div style={loadingStyle}>
          {loadError ?? '正在加载地图…'}
        </div>
      ) : null}
      {mapReady && viewCtx.drillLoading ? (
        <div style={provinceLoadingStyle}>加载省份地图…</div>
      ) : null}
      {mapReady && viewCtx.mapName !== 'china' && onBackToNational ? (
        <div
          style={{
            ...mapBackBtnWrapStyle,
            right: layoutChromeRightInsetPx,
          }}
        >
          <button
            type="button"
            style={mapActionBtnStyle}
            aria-label="返回全国地图"
            onClick={() => {
              setDrillTargetCoord(null);
              setLayoutMode('nationalOverview');
              policyDrillByUserRef.current = false;
              onBackToNational?.();
            }}
          >
            <span aria-hidden>←</span>
            返回全国
          </button>
          {onRestoreDefaults ? (
            <button
              type="button"
              style={mapActionBtnStyle}
              aria-label="恢复默认视图与筛选"
              onClick={() => {
                setDrillTargetCoord(null);
                setFocusAdcode(null);
                setLayoutMode('nationalOverview');
                policyDrillByUserRef.current = false;
                onRestoreDefaults();
              }}
            >
              <svg
                width={14}
                height={14}
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden
              >
                <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8h5z" />
              </svg>
              恢复默认
            </button>
          ) : null}
        </div>
      ) : null}
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
}
